import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { compressImage } from "@/lib/compress";
import { Camera, X, Sparkle, CheckCircle } from "@phosphor-icons/react";

/**
 * ApplianceNameplateScan — single-photo Claude Vision scanner for the
 * ApplianceIQ dashboard. Snap a nameplate → OCR extracts make/model/serial
 * → tap Save to add it as a property asset.
 */
export default function ApplianceNameplateScan({ onClose, onSaved }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scanned, setScanned] = useState(null); // response from /property-assets/scan
  const [form, setForm] = useState({
    address: "", unit: "", location: "",
    name: "", type: "", make: "", model: "", serial: "",
    install_date: "", notes: "",
  });

  const pick = () => fileRef.current?.click();

  const runScan = async (file) => {
    if (!file) return;
    setScanning(true);
    setPreview(URL.createObjectURL(file));
    try {
      const compressed = await compressImage(file);
      const fd = new FormData();
      fd.append("file", compressed);
      const { data } = await api.post("/property-assets/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setScanned(data);
      setForm((f) => ({
        ...f,
        type: data.type || "",
        name: data.type || "",
        make: data.make || "",
        model: data.model || "",
        serial: data.serial || "",
        install_date: data.install_date || "",
        notes: data.notes || "",
      }));
      toast.success("Nameplate read!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Scan failed");
    } finally { setScanning(false); }
  };

  const save = async () => {
    if (!form.address.trim()) { toast.error("Address required"); return; }
    setSaving(true);
    try {
      await api.post("/property-assets", {
        address: form.address.trim(),
        unit: form.unit.trim(),
        location: form.location.trim(),
        name: form.name.trim() || form.type.trim() || "Appliance",
        type: form.type.trim(),
        make: form.make.trim(),
        model: form.model.trim(),
        serial: form.serial.trim(),
        install_date: form.install_date.trim(),
        notes: form.notes.trim(),
        photo_path: scanned?.photo_path || "",
      });
      toast.success("Asset saved");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const fld = (key) => ({
    value: form[key],
    onChange: (e) => setForm({ ...form, [key]: e.target.value }),
    className: "w-full h-9 px-2 border-2 border-slate-300 rounded text-sm font-medium focus:border-purple-500 focus:outline-none",
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
      data-testid="nameplate-scan-modal">
      <div className="w-full max-w-2xl bg-white rounded-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b-2 border-slate-200 bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white flex items-center gap-2">
          <Sparkle weight="fill" size={20}/>
          <div className="flex-1">
            <div className="font-display font-black text-lg uppercase tracking-tight leading-none">Nameplate Scanner</div>
            <div className="text-[10px] font-bold tracking-widest uppercase opacity-80">Claude Vision · make · model · serial</div>
          </div>
          <button onClick={onClose} data-testid="nameplate-close" className="p-1.5 hover:bg-white/20 rounded">
            <X weight="bold" size={18}/>
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Snap or pick */}
          <div className="grid grid-cols-2 gap-3">
            <div className={`aspect-square bg-slate-100 rounded-lg border-2 border-dashed ${scanning ? "border-purple-500 animate-pulse" : "border-slate-300"} overflow-hidden flex items-center justify-center`}>
              {preview ? (
                <img src={preview} alt="preview" className="w-full h-full object-cover"/>
              ) : (
                <div className="text-center text-slate-400 text-xs px-4">
                  <Camera weight="bold" size={40} className="mx-auto mb-2"/>
                  <div className="font-black uppercase tracking-widest">No photo yet</div>
                  <div className="mt-1 text-[10px] font-medium">Snap the appliance data-plate sticker</div>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden"
                onChange={(e) => e.target.files?.[0] && runScan(e.target.files[0])}/>
              <button onClick={pick} disabled={scanning}
                data-testid="nameplate-pick-btn"
                className="h-12 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-black uppercase text-sm tracking-widest rounded inline-flex items-center justify-center gap-2">
                <Camera weight="fill" size={18}/>
                {scanning ? "Scanning…" : preview ? "Re-shoot" : "Snap Nameplate"}
              </button>
              {scanned && (
                <div className="p-2 bg-emerald-50 border-2 border-emerald-500 rounded text-[11px] space-y-0.5">
                  <div className="font-black text-emerald-800 flex items-center gap-1">
                    <CheckCircle weight="fill" size={12}/> Read
                  </div>
                  {form.make && <div><span className="font-black">Make:</span> {form.make}</div>}
                  {form.model && <div><span className="font-black">Model:</span> {form.model}</div>}
                  {form.serial && <div><span className="font-black">Serial:</span> {form.serial}</div>}
                </div>
              )}
            </div>
          </div>

          {/* Property assignment */}
          <div className="border-t-2 border-slate-100 pt-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Assign to Property</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input list="known-addresses" autoComplete="off" placeholder="Address (e.g. 1200 Sample St)" {...fld("address")} data-testid="nameplate-address"/>
              <input placeholder="Unit (optional)" {...fld("unit")} data-testid="nameplate-unit"/>
            </div>
            <input placeholder="Location (e.g. Kitchen, Basement)" {...fld("location")} data-testid="nameplate-location" className={`${fld("location").className} mt-2`}/>
          </div>

          {/* Editable OCR fields */}
          <div className="border-t-2 border-slate-100 pt-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Nameplate Details</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Type</label>
                <input placeholder="Refrigerator, Water Heater…" {...fld("type")} data-testid="nameplate-type"/>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Make / Brand</label>
                <input placeholder="Whirlpool, Rheem…" {...fld("make")} data-testid="nameplate-make"/>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Model</label>
                <input {...fld("model")} data-testid="nameplate-model"/>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Serial</label>
                <input {...fld("serial")} data-testid="nameplate-serial"/>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Install / Mfg Date</label>
                <input placeholder="YYYY-MM-DD or free text" {...fld("install_date")} data-testid="nameplate-install-date"/>
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Notes</label>
                <input placeholder="Capacity, specs…" {...fld("notes")} data-testid="nameplate-notes"/>
              </div>
            </div>
          </div>

          {/* Save row */}
          <div className="pt-2 flex gap-2 justify-end">
            <button onClick={onClose}
              className="h-10 px-4 border-2 border-slate-300 bg-white hover:bg-slate-50 text-slate-700 font-black uppercase text-xs tracking-widest rounded">
              Cancel
            </button>
            <button onClick={save} disabled={saving || !scanned || !form.address.trim()}
              data-testid="nameplate-save-btn"
              className="h-10 px-5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-black uppercase text-xs tracking-widest rounded inline-flex items-center gap-2">
              <CheckCircle weight="fill" size={14}/>
              {saving ? "Saving…" : "Save Asset"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
